import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { RegPlate, StatusPill, Icon, Skeleton, Badge, Sheet, Field, SectionHeader } from '../components/ui';
import { MarkChip } from '../components/MarkSelector';
import { SignatureCard } from './InspectionRun';
import { api, type Asset, type Defect, type Inspection, type Signature } from '../lib/api';
import { useAction, useLoad } from '../lib/hooks';
import { useAuth } from '../store/auth';
import { FORMS, DEFECT_CODES } from '../../../shared/forms';
import { formatDate, formatIsoWeek } from '../../../shared/schedule';
import { BRAND } from '../config/brand';
import './inspection.css';

interface Payload {
  inspection: Inspection;
  signatures: Signature[];
  defects: Defect[];
  asset: Asset;
}

export default function InspectionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fullSheet = useRef<HTMLDetailsElement>(null);

  /*
   * A closed <details> contributes nothing to the printed page - it reports
   * zero height and its contents are never laid out, so no CSS display rule
   * on the children can bring them back. The element has to actually be open.
   * It is opened for the duration of the print and put back afterwards, so
   * the screen keeps its collapsed summary.
   */
  useEffect(() => {
    let reopened = false;

    const expand = () => {
      const el = fullSheet.current;
      if (el && !el.open) {
        el.open = true;
        reopened = true;
      }
    };
    const restore = () => {
      const el = fullSheet.current;
      if (el && reopened) {
        el.open = false;
        reopened = false;
      }
    };

    window.addEventListener('beforeprint', expand);
    window.addEventListener('afterprint', restore);
    return () => {
      window.removeEventListener('beforeprint', expand);
      window.removeEventListener('afterprint', restore);
    };
  }, []);
  const user = useAuth((s) => s.user);
  const tenant = useAuth((s) => s.tenant);
  const { data, loading, error } = useLoad<Payload>(() => api.get(`/inspections/${id}`), [id]);
  const { busy, error: actionError, run } = useAction();
  const [amending, setAmending] = useState(false);
  const [reason, setReason] = useState('');

  if (loading && !data) {
    return (
      <Screen title="Inspection" back="/inspections">
        <Skeleton variant="detail" rows={4} />
      </Screen>
    );
  }
  if (error || !data) {
    return (
      <Screen title="Inspection" back="/inspections">
        <div className="notice notice-danger">{error ?? 'Not found.'}</div>
      </Screen>
    );
  }

  const { inspection, signatures, defects, asset } = data;
  const form = FORMS[inspection.form_code];

  if (!inspection.locked_at) {
    navigate(`/inspections/${inspection.id}/run`, { replace: true });
    return null;
  }

  const marked = Object.entries(inspection.items);
  const defectItems = marked.filter(([, v]) => DEFECT_CODES.includes(v.mc as any));
  const monitorItems = marked.filter(([, v]) => v.mc === 'M');
  const naItems = marked.filter(([, v]) => v.mc === 'NA');
  const canAmend = user?.role === 'owner' || user?.role === 'admin';

  const amend = () =>
    run(async () => {
      const result = await api.post<{ inspection: Inspection }>(`/inspections/${inspection.id}/amend`, {
        reason,
      });
      navigate(`/inspections/${result.inspection.id}/run`);
    });

  return (
    <Screen
      title={inspection.ref}
      subtitle={`${asset.reg_no ?? asset.fleet_no ?? ''} · ${formatDate(inspection.inspection_date)}`}
      back={`/fleet/${asset.id}`}
      action={
        <button className="btn btn-ghost btn-sm no-print" onClick={() => window.print()}>
          Print
        </button>
      }
    >
      <div className="stack-5 record">
        {/* ---- Printed letterhead ---- */}
        <div className="print-only record-letterhead">
          <h1>Safety Inspection and Maintenance Record</h1>
          <p>
            {tenant?.orgName ?? tenant?.name}
            {tenant?.address ? ` · ${tenant.address}` : ''}
            {tenant?.postcode ? `, ${tenant.postcode}` : ''}
          </p>
        </div>

        {/* ---- Status banner ---- */}
        <header className="detail-head">
          <div className="row spread">
            <RegPlate reg={asset.reg_no} fleetNo={asset.fleet_no} size="lg" />
            <div className="row row-tight">
              <Badge tone="hazard">{inspection.form_code}</Badge>
              <StatusPill state={inspection.status === 'amended' ? 'amended' : 'ok'}>
                {inspection.status === 'amended'
                  ? 'Superseded'
                  : inspection.overall_result === 'satisfactory'
                    ? 'Satisfactory'
                    : 'Rectified'}
              </StatusPill>
            </div>
          </div>
          <div>
            <h2>{[asset.make, asset.model].filter(Boolean).join(' ') || 'Vehicle'}</h2>
            <p className="small muted" style={{ marginTop: 4 }}>{inspection.operator_name ?? 'No operator recorded'}</p>
          </div>

          {inspection.hash_valid === false ? (
            <div className="record-lock is-broken">
              <Icon name="warning" size={16} />
              <span>
                <strong>Does not match its signature.</strong> The stored content differs from what was
                signed — do not rely on it.
              </span>
            </div>
          ) : (
            <div className="record-lock">
              <Icon name="lock" size={16} />
              <span>
                Locked {new Date(inspection.locked_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                {' · '}verified against its signature
              </span>
            </div>
          )}
        </header>

        {inspection.supersedes_id ? (
          <div className="notice notice-info">
            <span className="small">
              This is an amendment.{' '}
              <Link to={`/inspections/${inspection.supersedes_id}`} style={{ textDecoration: 'underline' }}>
                View the record it supersedes
              </Link>
              .
            </span>
          </div>
        ) : null}

        {/* ---- Header block ---- */}
        <section className="stack print-block">
          <SectionHeader title="Record" />
          <div>
            <dl className="kv">
              <dt>Reference</dt>
              <dd className="mono">{inspection.ref}</dd>
              <dt>Inspection date</dt>
              <dd>{formatDate(inspection.inspection_date)}</dd>
              <dt>ISO week</dt>
              <dd>{inspection.inspection_date ? formatIsoWeek(inspection.inspection_date) : '—'}</dd>
              {inspection.date_in ? (
                <>
                  <dt>Date in</dt>
                  <dd>{formatDate(inspection.date_in)}</dd>
                </>
              ) : null}
              <dt>Organisation</dt>
              <dd>{inspection.inspection_organisation ?? '—'}</dd>
              <dt>Location</dt>
              <dd>{inspection.location ?? '—'}</dd>
              <dt>{asset.kind === 'trailer' ? 'Hubometer' : 'Odometer'}</dt>
              <dd className="mono">
                {inspection.odometer != null
                  ? `${inspection.odometer.toLocaleString('en-GB')} ${inspection.odometer_unit ?? ''}`
                  : '—'}
              </dd>
              {asset.vin ? (
                <>
                  <dt>VIN</dt>
                  <dd className="mono">{asset.vin}</dd>
                </>
              ) : null}
              {inspection.header_extra?.oLicenceNo ? (
                <>
                  <dt>O-Licence</dt>
                  <dd className="mono">{inspection.header_extra.oLicenceNo}</dd>
                </>
              ) : null}
              <dt>Form</dt>
              <dd>
                {form.code} — {form.revision}
              </dd>
            </dl>
          </div>
        </section>

        {/* ---- Exceptions ---- */}
        <section className="stack print-block">
          <SectionHeader title="Marked items" />
          <p className="hint">
            {marked.length} of {form.sections.flatMap((s) => s.items).length} items marked.
            {defectItems.length === 0 && monitorItems.length === 0
              ? ' All satisfactory.'
              : ` ${defectItems.length} defect${defectItems.length === 1 ? '' : 's'}, ${monitorItems.length} to monitor.`}
          </p>

          {defectItems.length + monitorItems.length + naItems.length === 0 ? (
            <div className="notice notice-ok">
              <span className="small">Every item on this sheet was marked satisfactory.</span>
            </div>
          ) : (
            <div className="panel divide">
              {[...defectItems, ...monitorItems, ...naItems].map(([key, value]) => {
                const item = form.sections.flatMap((s) => s.items).find((i) => i.key === key);
                const section = form.sections.find((s) => s.items.some((i) => i.key === key));
                return (
                  <div key={key} className="listrow" style={{ alignItems: 'flex-start' }}>
                    <div className="listrow-lead" style={{ minWidth: '2.5rem' }}>
                      <MarkChip code={value.mc} />
                    </div>
                    <div className="grow">
                      <div className="listrow-title">{item?.label ?? key}</div>
                      <div className="listrow-sub wrap">
                        {section?.title}
                        {item?.im ? (
                          <>
                            <span className="dot" />
                            <Badge>IM {item.im}</Badge>
                          </>
                        ) : null}
                      </div>
                      {value.note ? (
                        <p className="small" style={{ marginTop: 4 }}>
                          {value.note}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/*
            Collapsed on screen, because the exceptions are what a person
            reads. Always expanded in print: a record that lists only the
            defects is not the equivalent of the RHA sheet, and an auditor
            expects every item and its mark. The expansion is handled by the
            print rules in inspection.css.
          */}
          <details className="fullsheet" ref={fullSheet}>
            <summary>Show the complete sheet</summary>
            <div className="print-cols">
              {form.sections.map((section) => (
                <div key={section.key} className="fullsheet-section">
                  <h3>{section.title}</h3>
                  <ul>
                    {section.items.map((item) => (
                      <li key={item.key}>
                        <MarkChip code={inspection.items[item.key]?.mc ?? ''} />
                        <span className="grow">{item.label}</span>
                        {item.im ? <span className="item-im">IM {item.im}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </details>
        </section>

        {/* ---- Measurements ---- */}
        {Object.keys(inspection.brake_test ?? {}).length > 0 ? (
          <section className="stack print-block">
            <SectionHeader title="Braking performance assessment" />
            <div>
              <dl className="kv">
                {inspection.brake_test.method ? (
                  <>
                    <dt>Method</dt>
                    <dd>
                      {inspection.brake_test.method === 'rbt'
                        ? 'Roller brake test'
                        : inspection.brake_test.method === 'ebpms'
                          ? 'EBPMS'
                          : 'Decelerometer'}
                    </dd>
                  </>
                ) : null}
                {inspection.brake_test.laden ? (
                  <>
                    <dt>Laden state</dt>
                    <dd>
                      {inspection.brake_test.laden === 'laden' ? 'Laden' : 'Unladen'}
                      {inspection.brake_test.weight ? ` · ${Number(inspection.brake_test.weight).toLocaleString('en-GB')} kg` : ''}
                    </dd>
                  </>
                ) : null}
                {form.brakeRefs.map((ref) => {
                  const value = inspection.brake_test[`eff${ref.im}`] ?? inspection.brake_test[ref.label.split(' ')[0].toLowerCase()];
                  return value ? (
                    <div key={ref.im} style={{ display: 'contents' }}>
                      <dt>{ref.label}</dt>
                      <dd className="mono">{value}%</dd>
                    </div>
                  ) : null;
                })}
                {inspection.brake_test.printoutAttached ? (
                  <>
                    <dt>RBT printout</dt>
                    <dd>{inspection.brake_test.printoutAttached === 'yes' ? 'Attached' : 'Not attached'}</dd>
                  </>
                ) : null}
              </dl>
            </div>
          </section>
        ) : null}

        {/* ---- Rectification ---- */}
        {defects.length > 0 ? (
          <section className="stack print-block">
            <SectionHeader title="Part 2 — Rectification" />
            <div className="panel divide">
              {defects.map((defect) => (
                <div key={defect.id} className="listrow" style={{ alignItems: 'flex-start' }}>
                  <div className="grow">
                    <div className="listrow-title">{defect.description}</div>
                    <div className="listrow-sub wrap">
                      {defect.im_no ? <Badge>IM {defect.im_no}</Badge> : null}
                      <Badge tone={defect.severity}>
                        {defect.severity === 'safety' ? 'Safety' : defect.severity === 'repair' ? 'Repair' : 'Monitor'}
                      </Badge>
                    </div>
                    {defect.action_taken ? (
                      <p className="small" style={{ marginTop: 4 }}>
                        {defect.action_taken}
                        {defect.done_by ? <span className="dim"> — {defect.done_by}</span> : null}
                      </p>
                    ) : null}
                  </div>
                  <StatusPill state={defect.status === 'open' ? 'overdue' : 'ok'} size="sm">
                    {defect.status === 'open' ? 'Open' : 'Rectified'}
                  </StatusPill>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {inspection.comments ? (
          <section className="stack print-block">
            <SectionHeader title="Inspector comments" />
            <p>{inspection.comments}</p>
          </section>
        ) : null}

        {/* ---- Signatures ---- */}
        <section className="stack print-block">
          <SectionHeader title="Declarations" />
          <div className="sig-block">
            {signatures.map((signature) => (
              <SignatureCard
                key={signature.id}
                signature={signature}
                caption={signature.kind === 'rectification' ? 'Roadworthiness declaration' : 'Inspector'}
              />
            ))}
          </div>
          <p className="tiny dim">
            It is the responsibility of the operator to ensure that the vehicle is in a roadworthy condition
            before being used on the road.
          </p>
        </section>

        {inspection.content_hash ? (
          <p className="tiny dim mono" style={{ overflowWrap: 'anywhere' }}>
            SHA-256 {inspection.content_hash}
          </p>
        ) : null}

        <p className="tiny dim print-only">{BRAND.legalNote}</p>

        {canAmend && inspection.status !== 'amended' ? (
          <div className="no-print">
            <button className="btn btn-ghost btn-block" onClick={() => setAmending(true)}>
              Raise an amendment
            </button>
            <p className="hint" style={{ marginTop: 6, textAlign: 'center' }}>
              A signed record cannot be edited. An amendment creates a new sheet that supersedes this one.
            </p>
          </div>
        ) : null}

        <Sheet
          open={amending}
          onClose={() => setAmending(false)}
          title="Raise an amendment"
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setAmending(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={amend} disabled={busy || !reason.trim()}>
                {busy ? 'Creating…' : 'Create amendment'}
              </button>
            </>
          }
        >
          <div className="stack-4">
            {actionError ? <div className="notice notice-danger">{actionError}</div> : null}
            <p className="small muted">
              The original stays locked and on file. The new sheet copies its marks so you only change what
              was wrong. The reason is recorded in the audit trail.
            </p>
            <Field label="Reason for the amendment">
              <textarea
                className="textarea"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Tyre pressures transposed on axle 2"
              />
            </Field>
          </div>
        </Sheet>
      </div>
    </Screen>
  );
}
